import { Formik } from 'formik';
import Fuse from 'fuse.js';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import {
  AutoComplete,
  Avatar,
  Button,
  Buttons,
  CancelButton,
  Drawer,
  Field,
  Form,
  Icon,
  MemberContactPopover,
  Radio,
  Table,
  TableTitle,
  Tooltip,
} from '~/components';
import { TableBoxRowActions } from '~/components/table';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useAuth, useDateTimeFormat, useDirtyCheck, useFeatures } from '~/hooks';
import { colors, weights } from '~/styles';
import { emptyStringToNull, mergeValues } from '~/utils';
import RevalueConfirmation from '../../rate-cards/RevalueConfirmation';
import { describeWindow } from '../../rate-cards/timeline';

const AssignmentsTable = styled.div`
  width: 100%;
  font-size: 0.875rem;
`;

const MemberInfo = styled.div`
  display: flex;
  align-items: center;
  opacity: ${({ $inactive }) => ($inactive ? 0.4 : 1)};
`;

const MemberName = styled.span`
  margin-left: 0.5rem;
`;

const MemberCount = styled.span`
  margin-left: 0.375rem;
  font-weight: ${weights.normal};
`;

const NoMembersCell = styled.div`
  padding: 0.75rem 1rem;
  color: ${colors.grey40};
`;

const AddMemberRow = styled(Table.Row)`
  border-bottom: none;
  margin-top: 0.375rem;

  > .table-cell {
    flex: 1;
    padding: 0;
  }
`;

const AddMemberCell = styled.div`
  width: 100%;
`;

const orderAssignments = (members) => _.orderBy(members, [(pm) => (pm.isActuallyActive ? 0 : 1), 'member.name']);

export default function ProjectRoleForm({ projectId, projectRoleId, onSaved, onCancel }) {
  const features = useFeatures();
  const auth = useAuth();
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();
  const dateTimeFormat = useDateTimeFormat();

  const [project, setProject] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.www.workspaces(workspace.id).projects(projectId).get();
        setProject(data);
      } catch {
        // Do nothing
      }
    })();
  }, [api, workspace.id, projectId]);

  const [projectRole, setProjectRole] = useState(null);
  useEffect(() => {
    (async () => {
      if (!projectRoleId) {
        setProjectRole({});
        return;
      }

      try {
        const { data } = await api.www
          .workspaces(workspace.id)
          .projectSettings(projectId)
          .projectRoles(projectRoleId)
          .get();
        setProjectRole(data);
      } catch {
        // Do nothing
      }
    })();
  }, [api, workspace.id, projectId, projectRoleId]);

  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);
  const confirmation = useConfirmation();

  if (!project || !projectRole) return null;

  const hasMultipleRateCards = (projectRole.rateCards?.length ?? 0) > 1;

  const initialValues = mergeValues(
    {
      discipline: null,
      practice: null,
      location: null,
      isBillable: project.isBillable,
      isActive: true,
      name: '',
      rate: '',
      members: [],
      rateCards: [],
    },
    {
      ...projectRole,
      members: orderAssignments(projectRole?.members?.map((pmr) => pmr.projectMember) ?? []),
      rateCards: (projectRole?.rateCards ?? []).map((rateCard) => ({ ...rateCard, rate: rateCard.rate ?? '' })),
    },
  );

  const handleSubmit = async (values, formik) => {
    values = emptyStringToNull({
      ..._.omit(values, ['discipline', 'practice', 'location']),
      disciplineId: values.discipline?.id ?? null,
      practiceId: values.practice?.id ?? null,
      locationId: values.location?.id ?? null,
      members: values.members.map((m) => ({ projectMemberId: m.id })),
    });

    // A new role seeds its rate onto every card; an edit writes the rate of each card it changed.
    if (!projectRoleId) {
      formik.setSubmitting(true);
      try {
        await api.www
          .workspaces(workspace.id)
          .projectSettings(projectId)
          .projectRoles()
          .post(_.omit(values, ['rateCards']));
        toast.success('Project Role saved successfully.');
        await onSaved();
      } catch (error) {
        toast.error(error.message);
      }
      formik.setSubmitting(false);
      return;
    }

    const saved = _.keyBy(projectRole.rateCards, 'projectRateCardId');
    const rateCards = values.rateCards
      .map((row) => emptyStringToNull(_.pick(row, ['projectRateCardId', 'rate'])))
      .filter((row) => row.rate !== (saved[row.projectRateCardId].rate ?? null));

    const tracked = rateCards.map((row) => saved[row.projectRateCardId]).filter(({ hasTimeEntries }) => hasTimeEntries);
    if (tracked.length > 0) {
      const confirmed = await confirmation.prompt((resolve) => (
        <RevalueConfirmation rateCards={hasMultipleRateCards ? tracked : null} resolve={resolve} />
      ));
      if (!confirmed) return;
    }

    formik.setSubmitting(true);

    try {
      await api.www
        .workspaces(workspace.id)
        .projectSettings(projectId)
        .projectRoles(projectRoleId)
        .patch({ ..._.omit(values, ['rate', 'rateCards']), rateCards });
      toast.success('Project Role saved successfully.');
      await onSaved();
    } catch (error) {
      toast.error(error.message);
    }

    formik.setSubmitting(false);
  };

  return (
    <Drawer
      title={projectRoleId ? 'Edit Project Role' : 'Add Project Role'}
      isOpen
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onCancel}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        return (
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={Yup.object().shape({
              isBillable: Yup.bool().label('Is Billable'),
              name: Yup.string().label('Name').max(255).required(),
              rate: Yup.number().label('Rate').min(0).max(99999999999).nullable(),
              rateCards: Yup.array().of(
                Yup.object().shape({
                  rate: Yup.number().label('Bill Rate').min(0).max(99999999999).nullable(),
                }),
              ),
            })}>
            {(formik) => {
              return (
                <Form>
                  <Form.Section title="Details">
                    <Form.Control>
                      <Field.Text autoFocus name="name" placeholder="Name" maxLength={255} />
                    </Form.Control>

                    {features.practices && (
                      <Form.Control>
                        <Field.PracticeSelect name="practice" placeholder="Practice" />
                      </Form.Control>
                    )}

                    <Form.Control>
                      <Field.LocationSelect name="location" placeholder="Location" />
                    </Form.Control>

                    {features.disciplines && (
                      <Form.Control>
                        <Field.DisciplineSelect name="discipline" placeholder="Discipline" />
                      </Form.Control>
                    )}
                  </Form.Section>

                  <Form.Section title="Billing">
                    {project.isBillable && (
                      <Form.Control>
                        <Field.RadioGroup name="isBillable">
                          <Radio value={true} label="Billable" disabled={!auth.projects.sections.billing} />
                          <Radio value={false} label="Non-Billable" disabled={!auth.projects.sections.billing} />
                        </Field.RadioGroup>
                      </Form.Control>
                    )}

                    {formik.values.isBillable && !hasMultipleRateCards && (
                      <Form.Control>
                        <Field.Currency
                          name={projectRoleId ? 'rateCards.0.rate' : 'rate'}
                          placeholder="Rate"
                          precision={7}
                          currency={project.currency}
                          disabled={!auth.projects.sections.billing}
                        />
                      </Form.Control>
                    )}

                    {hasMultipleRateCards && (
                      <>
                        <TableTitle>Rate Cards</TableTitle>

                        <Table small>
                          <Table.Header>
                            <Table.Column>Date Range</Table.Column>
                            <Table.Column width="12rem" align="right">
                              Bill Rate
                            </Table.Column>
                          </Table.Header>

                          <Table.Body>
                            {formik.values.rateCards.map((row, index) => (
                              <Table.Row key={row.projectRateCardId} data-testid="rate_card_rate_row">
                                <Table.Cell>
                                  #{row.projectRateCard.number} · {describeWindow(row, dateTimeFormat)}
                                  {row.hasTimeEntries && (
                                    <Tooltip Container="span" message="Time has been tracked in this period">
                                      <Icon icon="clock" color={colors.grey40} spaceLeft data-testid="tracked_time" />
                                    </Tooltip>
                                  )}
                                </Table.Cell>
                                <Table.Cell>
                                  {formik.values.isBillable && (
                                    <Field.Currency
                                      name={`rateCards.${index}.rate`}
                                      precision={7}
                                      currency={project.currency}
                                      disabled={!auth.projects.sections.billing}
                                    />
                                  )}
                                </Table.Cell>
                              </Table.Row>
                            ))}
                          </Table.Body>
                        </Table>
                      </>
                    )}
                  </Form.Section>

                  <Form.Section title="Member Assignments">
                    <Form.Control>
                      <AssignmentsSection
                        projectId={projectId}
                        members={formik.values.members}
                        onChange={(members) => formik.setFieldValue('members', members)}
                      />
                    </Form.Control>
                  </Form.Section>

                  <Drawer.Actions>
                    <Buttons align="right">
                      <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                      <Button isLoading={formik.isSubmitting} onClick={formik.submitForm}>
                        Save &amp; Close
                      </Button>
                    </Buttons>
                  </Drawer.Actions>
                </Form>
              );
            }}
          </Formik>
        );
      }}
    </Drawer>
  );
}

function AssignmentsSection({ projectId, members, onChange }) {
  const [isAdding, setIsAdding] = useState(false);

  return (
    <AssignmentsTable>
      <Table>
        <Table.BoxHeader>
          <Table.Column>
            Member
            {members.length > 0 && <MemberCount>({members.length})</MemberCount>}
          </Table.Column>
          <Table.BoxActionsColumn />
        </Table.BoxHeader>

        <Table.Body showNoResults={false}>
          {members.map((projectMember) => (
            <Table.BoxRow key={projectMember.id} data-testid="member_assignment_row">
              <Table.Cell>
                <MemberInfo $inactive={!projectMember.isActuallyActive}>
                  <MemberContactPopover member={projectMember.member} placement="left">
                    <Avatar
                      value={projectMember.member}
                      isCircle
                      hasBackground
                      initialsFontSize=".7rem"
                      size="1.5rem"
                    />
                  </MemberContactPopover>
                  <MemberName>{projectMember.member.name}</MemberName>
                </MemberInfo>
              </Table.Cell>
              <TableBoxRowActions>
                <TableBoxRowActions.Delete onClick={() => onChange(members.filter((m) => m.id !== projectMember.id))} />
              </TableBoxRowActions>
            </Table.BoxRow>
          ))}

          {members.length === 0 && (
            <Table.Row>
              <NoMembersCell>No members have been assigned to this role.</NoMembersCell>
            </Table.Row>
          )}
        </Table.Body>

        <AddMemberRow>
          <Table.Cell>
            <AddMemberCell>
              {isAdding ? (
                <ProjectMemberAddSelect
                  projectId={projectId}
                  assigned={members}
                  onAdd={(projectMember) => {
                    onChange(orderAssignments([...members, projectMember]));
                    setIsAdding(false);
                  }}
                  onClose={() => setIsAdding(false)}
                />
              ) : (
                <Button isAnchor isStrong onClick={() => setIsAdding(true)}>
                  <Icon icon="plus" size="xs" spaceRight />
                  Add
                </Button>
              )}
            </AddMemberCell>
          </Table.Cell>
        </AddMemberRow>
      </Table>
    </AssignmentsTable>
  );
}

function ProjectMemberAddSelect({ projectId, assigned, onAdd, onClose }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [data, setData] = useState(null);
  const [hasLoadError, setHasLoadError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const result = await api.www.workspaces(workspace.id).projectSettings(projectId).members().assignable();
        setData(result.data);
      } catch {
        setHasLoadError(true);
      }
    })();
  }, [api, workspace.id, projectId]);

  const options = useMemo(
    () =>
      _.orderBy(
        (data ?? []).filter((o) => !assigned.some((m) => m.id === o.id)),
        [(o) => (o.isActuallyActive ? 0 : 1), 'member.name'],
      ),
    [data, assigned],
  );

  const handleSearch = useCallback(
    async (q) => {
      if (!q) return options;
      return new Fuse(options, { keys: ['member.name'], threshold: 0.4 }).search(q).map((result) => result.item);
    },
    [options],
  );

  const emptyMessage = (inputValue) => {
    if (hasLoadError) return 'Unable to load the project team.';
    if (!data) return 'Loading project team members...';
    if (inputValue.trim()) return 'No project team members match your search.';
    return 'All project team members have been assigned.';
  };

  return (
    <AutoComplete
      name="add_member"
      placeholder="Add Member"
      position="top"
      value={null}
      autoFocus
      isOpen
      defaultOptions={options}
      onSearch={handleSearch}
      onBlur={onClose}
      onChange={({ target: { value } }) => value && onAdd(value)}
      renderValue={(option) => option.member.name}>
      {({ options, inputValue }) =>
        options.length > 0 ? (
          options.map((option, index) => (
            <AutoComplete.Option key={option.id} value={option} index={index} grey={!option.isActuallyActive}>
              <MemberInfo $inactive={!option.isActuallyActive}>
                <MemberContactPopover member={option.member} placement="left">
                  <Avatar value={option.member} isCircle hasBackground initialsFontSize=".7rem" size="1.5rem" />
                </MemberContactPopover>
                <MemberName>{option.member.name}</MemberName>
              </MemberInfo>
            </AutoComplete.Option>
          ))
        ) : (
          <AutoComplete.Panel>{emptyMessage(inputValue)}</AutoComplete.Panel>
        )
      }
    </AutoComplete>
  );
}
